
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { MagicLinkService } from '@/services/magicLinkService';
import { supabase } from '@/lib/supabase';

export default function MagicLinkLoginScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const [status, setStatus] = useState('Verificando convite...');

  useEffect(() => {
    // PRD §5.1 — `auth_magic_link_opened` micro event. Fires once on mount;
    // the deeplink handler is the canonical site for "user followed magic
    // link" intent.
    import('@/utils/clarityEvents')
      .then(({ logAuthMagicLinkOpened }) => logAuthMagicLinkOpened())
      .catch(() => {});
    handleMagicLink();
  }, []);

  const handleMagicLink = async () => {
    try {
      setStatus('Verificando link de acesso...');

      // Get magic link parameters  
      const token_hash = params.token_hash as string;
      const type = params.type as string;
      
      if (!token_hash) {
        throw new Error('Link inválido ou expirado - parâmetros ausentes');
      }

      setStatus('Autenticando usuário...');

      // Verify magic link with Supabase Auth (DUAL-PATH AUTHENTICATION)
      const { data: authData, error: authError } = await supabase.auth.verifyOtp({
        token_hash,
        type: type as any || 'email'
      });

      if (authError || !authData.user) {
        throw new Error(authError?.message || 'Falha na autenticação do link');
      }

      console.log('✅ Magic link authentication successful for:', authData.user.email);

      setStatus('Configurando perfil de usuário...');

      // DUAL-PATH PROFILE LINKING: Handle both web panel and direct mobile users
      const profileResult = await MagicLinkService.handleProfileLinking(authData.user);

      if (!profileResult.success) {
        throw new Error(profileResult.error?.message || 'Erro ao configurar perfil de usuário');
      }

      console.log('✅ Profile linking successful:', {
        userId: authData.user.id,
        email: authData.user.email,
        clinicId: profileResult.profile?.clinic_id
      });

      setStatus('Redirecionando para configuração...');

      // Always redirect to password setup for first-access users
      router.replace('/set-password');

    } catch (error) {
      console.error('❌ Magic link processing error:', error);
      
      Alert.alert(
        'Erro no Link de Acesso',
        error instanceof Error ? error.message : 'Erro desconhecido ao processar link',
        [
          {
            text: 'Tentar Novamente',
            onPress: handleMagicLink
          },
          {
            text: 'Voltar ao Início',
            onPress: () => router.replace('/welcome')
          }
        ]
      );
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        <ActivityIndicator size="large" color="#015480" style={styles.spinner} />
        
        <Text style={styles.title}>Processando Convite</Text>
        
        <Text style={styles.status}>{status}</Text>
        
        <Text style={styles.subtitle}>
          Por favor aguarde enquanto verificamos seu convite...
        </Text>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  spinner: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    marginBottom: 16,
  },
  status: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#015480',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#666666',
    textAlign: 'center',
    lineHeight: 20,
  },
});
